'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers, loadMoreUsers, deleteUser } from '../../redux/actions/userActions';
import { formatDateFR } from '../../utils/dateFormatter';

const LIMIT = 15;

const SortIcon = ({ column, sortBy, sortOrder }) => {
  if (sortBy !== column) return <i className="material-icons sort-icon">unfold_more</i>;
  return <i className="material-icons sort-icon active">{sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward'}</i>;
};

const UsersList = () => {
  const dispatch = useDispatch();
  const { users, loading, loadingMore, hasMore, error } = useSelector(state => state.users);
  const { user: currentUser } = useSelector(state => state.auth);

  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const sentinelRef = useRef(null);
  const observerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const buildParams = useCallback((page) => ({
    page,
    limit: LIMIT,
    search: searchTerm || undefined,
    role: roleFilter || undefined,
    sortBy,
    sortOrder
  }), [searchTerm, roleFilter, sortBy, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
    dispatch(getUsers(buildParams(1)));
  }, [dispatch, buildParams]);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          dispatch(loadMoreUsers(buildParams(nextPage)));
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [dispatch, hasMore, loadingMore, loading, currentPage, buildParams]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await dispatch(deleteUser(userToDelete.id));
      setShowDeleteModal(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  return (
    <div className="users-list-container">
      <div className="page-header">
        <h1>Gestion des utilisateurs</h1>
        <div className="header-actions">
          <Link to="/admin/users/new" className="btn btn-primary">
            <i className="material-icons">person_add</i>
            Ajouter un utilisateur
          </Link>
          <Link to="/admin/users/invitations" className="btn btn-outline-primary">
            <i className="material-icons">mail</i>
            Gérer les invitations
          </Link>
        </div>
      </div>

      <div className="filters-card">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input">
            <i className="material-icons">search</i>
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou email..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-dropdown">
            <select
              value={roleFilter}
              onChange={handleRoleFilterChange}
              className="form-control"
            >
              <option value="">Tous les rôles</option>
              <option value="admin">Administrateur</option>
              <option value="user">Utilisateur</option>
            </select>
          </div>
        </form>
      </div>

      {error && (
        <div className="alert alert-danger">{error}</div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement des utilisateurs...</p>
        </div>
      ) : users?.items?.length > 0 ? (
          <div className="admin-card">
            <div className="table-responsive" ref={scrollContainerRef}>
              <table className="table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('last_name')}>
                      Nom <SortIcon column="last_name" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('email')}>
                      Email <SortIcon column="email" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('role')}>
                      Rôle <SortIcon column="role" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th className="sortable" onClick={() => handleSort('created_at')}>
                      Date d'inscription <SortIcon column="created_at" sortBy={sortBy} sortOrder={sortOrder} />
                    </th>
                    <th>Vérifié</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.items.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-info">
                          <div className="user-avatar small">
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </div>
                          <span>{user.firstName} {user.lastName}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${user.role === 'admin' ? 'admin' : 'user'}`}>
                          {user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                        </span>
                      </td>
                      <td>{formatDateFR(user.createdAt, { withTime: false })}</td>
                      <td>
                        {user.emailVerified ? (
                          <span className="verified-badge">
                            <i className="material-icons">check</i>
                          </span>
                        ) : (
                          <span className="not-verified-badge">
                            <i className="material-icons">close</i>
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="table-actions">
                          <Link to={`/admin/users/${user.id}`} className="btn-icon">
                            <i className="material-icons">edit</i>
                          </Link>

                          {user.id !== currentUser.id && (
                            <button
                              className="btn-icon danger"
                              title="Supprimer l'utilisateur"
                              onClick={() => handleDeleteClick(user)}
                            >
                              <i className="material-icons">delete</i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} style={{ height: 1 }} />
              {loadingMore && (
                <div className="loading-more">
                  <div className="spinner spinner-sm"></div>
                  <span>Chargement...</span>
                </div>
              )}
              {!hasMore && users.items.length > 0 && (
                <p className="list-end-message">Tous les utilisateurs sont affichés</p>
              )}
            </div>
          </div>
      ) : (
        <div className="empty-state">
          <i className="material-icons">people</i>
          <h2>Aucun utilisateur</h2>
          <p>Aucun utilisateur correspondant aux critères de recherche.</p>
          <Link to="/admin/users/new" className="btn btn-primary">
            Ajouter un utilisateur
          </Link>
        </div>
      )}

      {showDeleteModal && userToDelete && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>Confirmer la suppression</h3>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>

            <div className="modal-body">
              <p>Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{userToDelete.firstName} {userToDelete.lastName}</strong> ?</p>
              <p className="text-danger">Cette action est irréversible. Toutes les données associées à cet utilisateur seront également supprimées.</p>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </button>

              <button
                className="btn btn-danger"
                onClick={confirmDelete}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
